"use client";

import { backendFetch, backendFetchResponse, parseResponse } from "./transport";
import type { BackendRequestInit } from "./transport";

export type ApiRequestOptions = {
  elevatedPrivileges: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
  ifMatch?: string;
};

function requestInit(options: ApiRequestOptions) {
  const headers: Record<string, string> = {};

  if (options.elevatedPrivileges) headers["X-Use-Elevated-Privileges"] = "true";

  if (options.ifMatch) headers["If-Match"] = options.ifMatch;

  const init: BackendRequestInit = { headers };

  if (options.signal !== undefined) init.signal = options.signal;

  if (options.timeoutMs !== undefined) init.timeoutMs = options.timeoutMs;

  return { ...init, headers };
}

export async function apiGet<T>(
  path: string,
  options: ApiRequestOptions,
): Promise<T> {
  return backendFetch<T>("", `/api/observatory${path}`, requestInit(options));
}

export async function apiGetText(
  path: string,
  options: ApiRequestOptions,
): Promise<string> {
  const response = await backendFetchResponse(
    "",
    `/api/observatory${path}`,
    requestInit(options),
  );

  if (response.ok) return response.text();

  await parseResponse<unknown>(response);
  throw new Error(`${response.status} ${response.statusText}`);
}

export async function apiGetBlob(
  path: string,
  options: ApiRequestOptions,
): Promise<Blob> {
  const response = await backendFetchResponse(
    "",
    `/api/observatory${path}`,
    requestInit(options),
  );

  if (response.ok) return response.blob();

  await parseResponse<unknown>(response);
  throw new Error(`${response.status} ${response.statusText}`);
}

export async function apiGetCursorPage<T>(
  path: string,
  options: ApiRequestOptions,
): Promise<{ entries: T[]; next_cursor: string | null }> {
  const response = await backendFetchResponse(
    "",
    `/api/observatory${path}`,
    requestInit(options),
  );
  return {
    entries: await parseResponse<T[]>(response),
    next_cursor: response.headers.get("X-Next-Cursor"),
  };
}

function writeJson<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  const init = requestInit(options);
  init.method = method;

  if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  return backendFetch<T>("", `/api/observatory${path}`, init);
}

export function apiPost<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  return writeJson("POST", path, body, options);
}

export function apiPut<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  return writeJson("PUT", path, body, options);
}

export function apiPatch<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  return writeJson("PATCH", path, body, options);
}

export function apiDelete<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  return writeJson("DELETE", path, body, options);
}

export function apiPostForm<T>(
  path: string,
  body: FormData,
  options: ApiRequestOptions,
): Promise<T> {
  return backendFetch<T>("", `/api/observatory${path}`, {
    ...requestInit(options),
    method: "POST",
    body,
  });
}
